/* eslint-disable no-trailing-spaces */
import React, {
  useState,
  useRef,
  useEffect,
  Children,
  isValidElement,
  useMemo
} from 'react';
import { Tooltip, Icon } from '@burna/monster-design-system';
import styles from './index.module.scss';
import classNames from 'classnames';
import { copyToClipboard } from 'utils';

const ErrorMessage = ({ title = 'There is nothing to show!' }) => {
  return <p className={styles.errorMessage}>{title}</p>;
};

const Action = ({
  icon,
  value,
  as: As = 'div',
  children,
  onClick,
  ...props
}) => {
  const [copied, setCopied] = useState(false);
  const copyTimeoutRef = useRef();

  const handleClick = () => {
    onClick?.();
    setCopied(true);
    clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setCopied(false), 1500);
    copyToClipboard(value);
  };

  if (children) {
    return children;
  }

  return (
    <>
      {icon === 'copy' ? (
        <Tooltip text="copied" visible={copied} positions={['left']}>
          <As
            className={styles.actionIconWrapper}
            {...props}
            onClick={handleClick}>
            <Icon className={styles.actionIcon} type={icon} />
          </As>
        </Tooltip>
      ) : (
        <As
          className={styles.actionIconWrapper}
          {...props}
          onClick={handleClick}>
          <Icon className={styles.actionIcon} type={icon} />
        </As>
      )}
    </>
  );
};

const Actions = ({ children }) => {
  const actions = useMemo(
    () =>
      Children.toArray(children).filter(child => {
        if (child && isValidElement(child)) {
          return child.type === Action;
        }
      }),
    [children]
  );

  return (
    <div className={styles.actions} onClick={e => e.stopPropagation()}>
      {actions}
    </div>
  );
};

const ContentItem = ({ title, children }) => (
  <div className={styles.contentItem}>
    <span>{title}</span>
    <div>{children}</div>
  </div>
);

const Content = ({ children }) => {
  const content = useMemo(
    () =>
      Children.toArray(children).filter(child => {
        if (child && isValidElement(child)) {
          return child.type === ContentItem;
        }
      }),
    [children]
  );

  return <div>{content.length > 0 ? content : <ErrorMessage />}</div>;
};

const RowItem = ({
  title,
  imgSrc,
  children,
  isSelectable = false,
  checked = false,
  onChange,
  onSwipe,
  onHold
}) => {
  const [expanded, setExpanded] = useState(false);
  const contentRef = useRef(null);

  const toggleExpand = () => {
    setExpanded(!expanded);
  };

  const actions = useMemo(
    () =>
      Children.toArray(children).filter(child => {
        if (child && isValidElement(child)) {
          return child.type === Actions;
        }
      }),
    [children]
  );

  const content = useMemo(
    () =>
      Children.toArray(children).filter(child => {
        if (child && isValidElement(child)) {
          return child.type === Content;
        }
      }),
    [children]
  );

  useEffect(() => {
    if (expanded && !isSelectable) {
      contentRef.current.style.height = contentRef.current.scrollHeight + 'px';
    } else {
      contentRef.current.style.height = 0;
    }
  }, [expanded, isSelectable]);

  const [startX, setStartX] = useState(null);
  const [widthRoot, setWidthRoot] = useState(0);
  const [translateX, setTranslateX] = useState(0);
  const itemRef = useRef(null);
  const percentLimited = 18;

  useEffect(() => {
    function getWidthOfItem() {
      const { width } = itemRef.current.getBoundingClientRect();
      setWidthRoot(width);
    }
    getWidthOfItem();
  }, []);

  const handleTouchStart = e => {
    setStartX(e.touches[0].clientX);

    const timerId = setTimeout(() => {
      onHold();
    }, 1100);
    e.target.dataset.timerId = timerId;
  };

  const handleTouchMove = e => {
    if (startX !== null) {
      clearTimeout(e.target.dataset.timerId);
      const currentX = e.touches[0].clientX;
      const deltaX = currentX - startX;
      const deltaXPercentage = Math.ceil((deltaX / widthRoot) * 100);

      setTranslateX(deltaXPercentage);
      const limitedTranslateX = Math.min(
        Math.max(deltaXPercentage, -percentLimited),
        0
      );
      setTranslateX(limitedTranslateX);
    }
  };

  const handleTouchEnd = e => {
    if (translateX <= -percentLimited) {
      onSwipe();
    }
    clearTimeout(e.target.dataset.timerId);
    setStartX(null);
    setTranslateX(0);
  };

  return (
    <div ref={itemRef} className={styles.itemWrapper}>
      <div className={styles.deleteButton}>
        <Icon className={styles.deleteIcon} type="trash" />
      </div>

      <div
        onTouchStart={onSwipe ? handleTouchStart : () => {}}
        onTouchMove={onSwipe ? handleTouchMove : () => {}}
        onTouchEnd={onSwipe ? handleTouchEnd : () => {}}
        style={{
          transform: `translateX(${translateX}%)`,
          transition: '0.2s ease'
        }}
        className={classNames(styles.itemContainer, {
          [styles.itemSelect]: isSelectable,
          [styles.itemChecked]: isSelectable && checked
        })}>
        <div
          className={[styles.item]}
          onClick={isSelectable ? () => onChange(!checked) : toggleExpand}>
          <div className={styles.titleRow}>
            {isSelectable ? (
              <div className={styles.checkedIconContainer}>
                {checked ? (
                  <Icon className={styles.checkedIcon} type="check" />
                ) : null}
              </div>
            ) : (
              <Icon
                className={classNames(styles.icon, {
                  [styles.down]: !isSelectable && expanded
                })}
                type="chevron-right"
              />
            )}
            {imgSrc ? <img src={imgSrc} /> : null}
            <p className={styles.title}>{title}</p>
          </div>
          {!isSelectable ? <div>{actions}</div> : null}
        </div>

        <div className={styles.contentWrapper} ref={contentRef}>
          {content.length > 0 ? content : <ErrorMessage />}
        </div>
      </div>
    </div>
  );
};

RowItem.Actions = Actions;
RowItem.Action = Action;
RowItem.Content = Content;
RowItem.ContentItem = ContentItem;

export default RowItem;
